import {
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonSkeletonText,
    IonTitle,
    IonToolbar
} from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import { useMemo } from 'react';
import { format, startOfMonth, subDays, subMonths } from 'date-fns';
import { useDevicesQuery } from '../query/devicesQuery';
import {
    useCatActivityLeaderboardQuery,
    useDeviceActivityStatsQuery,
    useHouseholdActivityStatsQuery,
    useHouseholdMonthlyActivityStatsQuery
} from '../query/activityStatsQuery';
import { useRfidProfilesQuery } from '../query/rfidProfileQuery';
import {
    findCoreHours,
    monthlyTransitCounts,
    summarizeCatActivity,
    summarizeCatLeaderboard,
    summarizeDeviceDailyStats
} from '../utils/activityStatsSummary';
import { UNIDENTIFIED_RFID_CODE } from '../models/ActivityStats';
import { formatHour } from '../utils/statsFormat';
import { Device } from '../models/Device';
import { RfidProfile } from '../models/RfidProfile';
import { getRfidDisplayLabel } from '../utils/rfidDisplay';
import StatsSection from '../components/stats/StatsSection';
import CatHoursCard, { CatHoursRow } from '../components/stats/CatHoursCard';
import {
    DenyPeekTiles,
    HourHistogramCard,
    MonthlyTransitsCard,
    PreyCard,
    RecordsList,
    TransitsCard,
    WeekdayCard
} from '../components/stats/StatCards';
import { useComponentTranslation, useModalTranslation } from '../hooks/useTranslation';
import statStyles from '../components/stats/stats.module.css';

const STATS_WINDOW_DAYS = 30;
const STATS_MONTHLY_MONTHS = 12;
/** Rollup request cap (gateway MAX_DEVICE_IDS). */
const MAX_STATS_DEVICE_IDS = 20;
const MAX_CAT_ROWS = 8;

/**
 * Household-wide activity stats over the last 30 days, summed across every
 * device the user can see (design: Stats Modal Treatments v2). Mirrors the
 * activity overview's view of the world: hidden visitors are excluded.
 * Contraband figures come from the event-grain device rollups, so an event
 * involving two cats counts once at household level.
 */
const HouseholdStatsModal = ({ dismiss }: { dismiss: (data?: string | null, role?: string) => void }) => {
    const { t: tModals } = useModalTranslation();
    const { t: tComponents } = useComponentTranslation();

    const devicesQuery = useDevicesQuery();
    const deviceIds = useMemo(
        () => (devicesQuery.data?.pages.flat() ?? []).map((device: Device) => device.deviceId).slice(0, MAX_STATS_DEVICE_IDS),
        [devicesQuery.data]
    );

    // Fetch double the window so the previous 30 days feed the trend line
    // (and the prey-free streak's lookback).
    const today = new Date();
    const toDay = format(today, 'yyyy-MM-dd');
    const windowFromDay = format(subDays(today, STATS_WINDOW_DAYS - 1), 'yyyy-MM-dd');
    const fetchFromDay = format(subDays(today, STATS_WINDOW_DAYS * 2 - 1), 'yyyy-MM-dd');
    const monthlyFromDay = format(startOfMonth(subMonths(today, STATS_MONTHLY_MONTHS - 1)), 'yyyy-MM-dd');

    const dayStatsQuery = useHouseholdActivityStatsQuery(deviceIds, fetchFromDay, toDay);
    const leaderboardQuery = useCatActivityLeaderboardQuery(deviceIds, windowFromDay, toDay);
    const deviceRollupQuery = useDeviceActivityStatsQuery(deviceIds, fetchFromDay, toDay);
    const monthlyQuery = useHouseholdMonthlyActivityStatsQuery(deviceIds, monthlyFromDay, toDay);
    const profilesQuery = useRfidProfilesQuery();

    const stats = useMemo(
        () => (dayStatsQuery.data ? summarizeCatActivity(dayStatsQuery.data.days, windowFromDay, toDay) : null),
        [dayStatsQuery.data, windowFromDay, toDay]
    );

    // Event-grain counters: the honest source for contraband totals/streaks.
    const deviceStats = useMemo(
        () => (deviceRollupQuery.data ? summarizeDeviceDailyStats(deviceRollupQuery.data.days, windowFromDay, toDay) : null),
        [deviceRollupQuery.data, windowFromDay, toDay]
    );

    const monthly = useMemo(
        () => (monthlyQuery.data ? monthlyTransitCounts(monthlyQuery.data.months, toDay) : []),
        [monthlyQuery.data, toDay]
    );

    const cats = leaderboardQuery.data?.cats;
    const highlights = useMemo(() => (cats ? summarizeCatLeaderboard(cats) : null), [cats]);

    const profilesByRfid = useMemo(() => {
        const map = new Map<string, RfidProfile>();
        for (const profile of profilesQuery.data ?? []) {
            map.set(profile.rfidCode, profile);
        }
        return map;
    }, [profilesQuery.data]);

    const nameOf = (rfidCode: string) => getRfidDisplayLabel(rfidCode, profilesByRfid.get(rfidCode)?.label);

    // Per-cat rows: share of every transit the rollups saw (unidentified
    // included), plus the cat's own core activity window.
    const catRows = useMemo<CatHoursRow[]>(() => {
        if (!cats) {
            return [];
        }
        const totalTransits = cats.reduce((sum, cat) => sum + cat.transitsIn + cat.transitsOut, 0);
        return cats
            .filter((cat) => cat.rfidCode !== UNIDENTIFIED_RFID_CODE && cat.transitsIn + cat.transitsOut > 0)
            .slice(0, MAX_CAT_ROWS)
            .map((cat) => {
                const transits = cat.transitsIn + cat.transitsOut;
                const pct = totalTransits > 0 ? Math.round((transits / totalTransits) * 100) : 0;
                const coreHours = findCoreHours(cat.hourHistogram);
                return {
                    rfidCode: cat.rfidCode,
                    profile: profilesByRfid.get(cat.rfidCode),
                    transits,
                    sub: coreHours
                        ? tModals('household_stats.cat_row_sub', {
                            pct,
                            from: formatHour(coreHours.startHour),
                            to: formatHour(coreHours.endHourExclusive)
                        })
                        : tModals('household_stats.cat_row_sub_no_window', { pct }),
                    hourHistogram: cat.hourHistogram
                };
            });
    }, [cats, profilesByRfid, tModals]);

    const unidentifiedTransits = useMemo(() => {
        const bucket = cats?.find((cat) => cat.rfidCode === UNIDENTIFIED_RFID_CODE);
        return bucket ? bucket.transitsIn + bucket.transitsOut : 0;
    }, [cats]);

    // Household superlatives fold into the Records list as extra rows.
    const recordRows = useMemo(() => {
        if (!highlights) {
            return [];
        }
        const rows: { key: string; label: string; value: string }[] = [];
        if (highlights.mostPeeks) {
            rows.push({
                key: 'most_cautious',
                label: tModals('household_stats.most_cautious'),
                value: tModals('household_stats.most_cautious_value', {
                    name: nameOf(highlights.mostPeeks.rfidCode),
                    count: highlights.mostPeeks.peeks
                })
            });
        }
        if (highlights.mostDenies) {
            rows.push({
                key: 'most_denied',
                label: tModals('household_stats.most_denied'),
                value: tModals('household_stats.most_denied_value', {
                    name: nameOf(highlights.mostDenies.rfidCode),
                    count: highlights.mostDenies.denies
                })
            });
        }
        if (highlights.earliestRiser) {
            rows.push({
                key: 'earliest_riser',
                label: tModals('household_stats.earliest_riser'),
                value: tModals('household_stats.peak_value', {
                    name: nameOf(highlights.earliestRiser.cat.rfidCode),
                    hour: formatHour(highlights.earliestRiser.peakHour)
                })
            });
        }
        if (highlights.nightOwl) {
            rows.push({
                key: 'night_owl',
                label: tModals('household_stats.night_owl'),
                value: tModals('household_stats.peak_value', {
                    name: nameOf(highlights.nightOwl.cat.rfidCode),
                    hour: formatHour(highlights.nightOwl.peakHour)
                })
            });
        }
        return rows;
    }, [highlights, profilesByRfid, tModals]);

    const isLoading = devicesQuery.isLoading || dayStatsQuery.isLoading || leaderboardQuery.isLoading || deviceRollupQuery.isLoading;
    const noDevices = devicesQuery.isSuccess && deviceIds.length === 0;
    const hasActivity = (stats?.hasActivity ?? false) || (deviceStats?.hasActivity ?? false);

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar>
                    <IonTitle>{tModals('household_stats.title')}</IonTitle>
                    <IonButtons slot="end">
                        <IonButton
                            fill="solid"
                            className={statStyles.closeButton}
                            onClick={() => dismiss(null, 'cancel')}
                            aria-label={tModals('household_stats.close')}
                        >
                            <IonIcon slot="icon-only" icon={closeOutline} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent fullscreen color="light">
                <div className={statStyles.body}>
                    <StatsSection title={tComponents('stats.last_30_days')}>
                        {isLoading && !noDevices && (
                            <>
                                <IonSkeletonText animated className={statStyles.cardSkeleton} />
                                <IonSkeletonText animated className={statStyles.cardSkeleton} />
                            </>
                        )}

                        {(noDevices || (!isLoading && stats && !hasActivity)) && (
                            <div className={`${statStyles.card} ${statStyles.emptyCard}`}>{tComponents('stats.no_activity')}</div>
                        )}

                        {stats && deviceStats && hasActivity && (
                            <>
                                <PreyCard
                                    preyAttempts={deviceStats.preyAttempts}
                                    lastPreyDay={deviceStats.lastPreyDay}
                                    daysSinceLastPrey={deviceStats.daysSinceLastPrey}
                                />
                                <TransitsCard
                                    transits={stats.transits}
                                    transitsIn={stats.transitsIn}
                                    transitsOut={stats.transitsOut}
                                    trendPct={stats.transitsTrendPct}
                                />
                                <DenyPeekTiles denies={stats.denies} peeks={stats.peeks} />
                            </>
                        )}
                    </StatsSection>

                    {catRows.length > 0 && (
                        <StatsSection title={tModals('household_stats.when_each_cat')}>
                            <CatHoursCard rows={catRows} unidentifiedTransits={unidentifiedTransits} />
                        </StatsSection>
                    )}

                    {stats && deviceStats && stats.transits > 0 && (
                        <>
                            <StatsSection title={tModals('household_stats.transits_by_hour')}>
                                <HourHistogramCard
                                    hourHistogram={stats.hourHistogram}
                                    busiestHour={stats.busiestHour}
                                    coreHours={stats.coreHours}
                                />
                            </StatsSection>

                            <StatsSection title={tComponents('stats.transits_by_weekday')}>
                                <WeekdayCard
                                    weekdayCounts={stats.weekdayCounts}
                                    busiestWeekday={stats.busiestWeekday}
                                    avgTransitsPerDay={stats.avgTransitsPerDay}
                                />
                            </StatsSection>

                            {monthly.length >= 2 && (
                                <StatsSection title={tComponents('stats.transits_by_month')}>
                                    <MonthlyTransitsCard monthly={monthly} />
                                </StatsSection>
                            )}

                            <StatsSection title={tComponents('stats.records')}>
                                <RecordsList
                                    mostActiveDay={stats.mostActiveDay}
                                    daysSinceLastPrey={deviceStats.daysSinceLastPrey}
                                    lookbackDays={STATS_WINDOW_DAYS * 2}
                                    extraRows={recordRows}
                                />
                            </StatsSection>
                        </>
                    )}
                </div>
            </IonContent>
        </IonPage>
    );
};

export default HouseholdStatsModal;
